import {
  infiniteQueryOptions,
  queryOptions,
  replaceEqualDeep,
  type InfiniteData,
  useInfiniteQuery,
} from "@-/com.npmjs.tanstack__react-query";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { getMorphemeDetailsTileSlices } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import {
  SEARCH_PAGE_SIZE,
  isSentenceSearchQuery,
  searchResultToMorphemeLookup,
  type SearchMorphemesOptions,
  type SearchMorphemesResult,
} from "#search-morphemes-core";
import { searchMorphemesQueryFn, useGetSearchMorphemesQueryKey } from "#search-morphemes";
import { useEffect, useRef, useState } from "react";
import { filterNullable } from "@-/com.npmjs.array-utils-ts";
import { searchPageResponseSchema, type SearchPage } from "#search-page";
import { getSentenceSearchPage } from "#search-sentences";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { fetchWithDeadline, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

type SearchMorpheme = SearchMorphemesResult;

type CardsSearchPageParam = { allMorphemes: SearchMorpheme[] | null; offset: number };
type CardsSearchPage = SearchPage;
type CardsSearchData = InfiniteData<CardsSearchPage, CardsSearchPageParam>;

function shareSearchPages(previous: unknown, next: unknown): CardsSearchData {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- installed only on this query's validated page data
  const previousData = previous as CardsSearchData | undefined;
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- installed only on this query's validated page data
  const nextData = next as CardsSearchData;
  const ranking = replaceEqualDeep(
    previousData?.pages[0]?.allMorphemes,
    nextData.pages[0]?.allMorphemes,
  );
  if (ranking === undefined) {
    return replaceEqualDeep(previousData, nextData);
  }

  // Deep sharing treats data as a tree: a changed ranking would otherwise be copied once for
  // every page AND pageParam after refetch. Share it once, and reconcile card data separately.
  const pages = nextData.pages.map((page, index) => {
    const previousPage = previousData?.pages[index];
    const shared = replaceEqualDeep(previousPage, {
      ...page,
      allMorphemes: previousPage?.allMorphemes ?? ranking,
    });
    return shared.allMorphemes === ranking ? shared : { ...shared, allMorphemes: ranking };
  });
  const pageParams = nextData.pageParams.map((param, index) => {
    // The first cursor stays null so invalidation computes a fresh ranking.
    const allMorphemes = param.allMorphemes === null ? null : ranking;
    const previousParam = previousData?.pageParams[index];
    return previousParam?.offset === param.offset && previousParam.allMorphemes === allMorphemes
      ? previousParam
      : { ...param, allMorphemes };
  });
  if (
    previousData !== undefined &&
    pages.length === previousData.pages.length &&
    pageParams.length === previousData.pageParams.length &&
    pages.every((page, index) => page === previousData.pages[index]) &&
    pageParams.every((param, index) => param === previousData.pageParams[index])
  ) {
    return previousData;
  }
  return { pageParams, pages };
}

const INITIAL_SEARCH_PAGE_SIZE = 12;
const SEARCH_STALE_TIME = 30_000;
const TRAILING_SLASHES = /\/+$/;

export function useMorphemesSearchInfiniteQuery(
  query: string,
  {
    category = "all",
    enabled = true,
    exactOnly = false,
    pinyin,
  }: {
    category?: "all" | "characters" | "words" | "sentences";
    enabled?: boolean;
  } & SearchMorphemesOptions = {},
) {
  const trimmedQuery = query.trim();
  const searchOptions = { exactOnly, pinyin };
  const effectiveCategory =
    category !== "sentences" && isSentenceSearchQuery(trimmedQuery) ? "all" : category;
  const { hanziScript: script } = useActorUser();
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb, getOnlineWorkerUrl] = useContextSelectors(
    StaticDatabaseContext,
    (c) => c.db,
    (c) => c.getOnlineWorkerUrl,
  );
  const getSearchMorphemesQueryKey = useGetSearchMorphemesQueryKey();
  const { cacheKey: corpusCacheKey } = useMorphemeCorpusFilter();

  return useInfiniteQuery(
    infiniteQueryOptions({
      enabled: enabled && trimmedQuery.length > 0,
      staleTime: SEARCH_STALE_TIME,
      structuralSharing: shareSearchPages,
      queryFn: async ({ client, pageParam, signal }): Promise<CardsSearchPage> => {
        const workerUrl = await withDeadline(
          { deadline: 10_000, operation: "Resolve search server" },
          async () => getOnlineWorkerUrl?.(),
        );
        if (workerUrl) {
          const url = new URL("search", `${workerUrl.replace(TRAILING_SLASHES, "")}/`);
          url.searchParams.set("q", trimmedQuery);
          url.searchParams.set("category", effectiveCategory);
          url.searchParams.set("offset", String(pageParam.offset));
          url.searchParams.set("script", script);
          // The Worker rebuilds the user's corpus filter from its cache key (see `getSearchPage`).
          url.searchParams.set("corpus", corpusCacheKey);
          if (searchOptions.exactOnly) {
            url.searchParams.set("exact", "1");
          }
          if (searchOptions.pinyin !== undefined) {
            url.searchParams.set("pinyin", searchOptions.pinyin ? "1" : "0");
          }
          const response = await fetchWithDeadline(url, {
            bufferBody: true,
            deadline: 15_000,
            operation: "Search corpus",
            signal,
          });
          if (!response.ok) {
            throw new Error(`Search failed (${response.status})`);
          }
          const page = searchPageResponseSchema.parse(await response.json());
          // Each response contains the full ranking. Keep the first page's snapshot just as
          // the local path does, instead of retaining another N hits for every loaded page.
          return { ...page, allMorphemes: pageParam.allMorphemes ?? page.allMorphemes };
        }
        if (effectiveCategory === "sentences") {
          const page = await getSentenceSearchPage(staticDb, {
            offset: pageParam.offset,
            query: trimmedQuery,
            script,
          });
          return {
            ...page,
            allMorphemes: [],
            cards: [],
            hitCount: page.sentences.length,
            offset: pageParam.offset,
          };
        }
        let allMorphemes = pageParam.allMorphemes;
        if (allMorphemes === null) {
          allMorphemes = await client.fetchQuery(
            queryOptions({
              queryFn: searchMorphemesQueryFn,
              queryKey: getSearchMorphemesQueryKey(trimmedQuery, searchOptions),
              staleTime: SEARCH_STALE_TIME,
            }),
          );
        }

        // An obsolete search can finish its shared ranking query, but should not
        // compete with the current search for expensive detail reads.
        if (signal.aborted) {
          throw new Error("Search aborted");
        }
        const categoryMorphemes =
          effectiveCategory === "all"
            ? allMorphemes
            : allMorphemes.filter(
                (morpheme) => (morpheme.category === "word") === (effectiveCategory === "words"),
              );
        const pageSize = pageParam.offset === 0 ? INITIAL_SEARCH_PAGE_SIZE : SEARCH_PAGE_SIZE;
        const nextOffset = Math.min(pageParam.offset + pageSize, categoryMorphemes.length);
        const pageMorphemes = categoryMorphemes.slice(pageParam.offset, nextOffset);
        const cards = filterNullable(
          await getMorphemeDetailsTileSlices(
            contentDb,
            staticDb,
            pageMorphemes.map((morpheme) => searchResultToMorphemeLookup(morpheme)),
          ),
        );
        if (signal.aborted) {
          throw new Error("Search aborted");
        }
        return {
          allMorphemes,
          cards,
          hasMoreSentences: false,
          hitCount: categoryMorphemes.length,
          nextOffset,
          offset: pageParam.offset,
          sentences: [],
        };
      },
      initialPageParam: { allMorphemes: null, offset: 0 } satisfies CardsSearchPageParam,
      getNextPageParam: (lastPage: CardsSearchPage): CardsSearchPageParam | undefined => {
        // Advance by consumed hits, including hits whose detail slice is absent.
        if (
          !lastPage.hasMoreSentences &&
          (effectiveCategory === "sentences" || lastPage.nextOffset >= lastPage.hitCount)
        ) {
          return;
        }
        return { allMorphemes: lastPage.allMorphemes, offset: lastPage.nextOffset };
      },
      queryKey: [
        "morphemesSearch",
        getSearchMorphemesQueryKey(trimmedQuery, searchOptions),
        effectiveCategory,
        corpusCacheKey,
        getOnlineWorkerUrl,
        script,
      ] as const,
    }),
  );
}

export function useDebouncedMorphemesSearch(
  query: string,
  {
    category,
    debounceMs = 150,
  }: { category?: "all" | "characters" | "words" | "sentences"; debounceMs?: number } = {},
) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(setDebouncedQuery, debounceMs, query);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [query, debounceMs]);

  const result = useMorphemesSearchInfiniteQuery(debouncedQuery, { category });
  return { ...result, debouncedQuery };
}
